import { Select } from "antd"
import React, { useState } from "react"

interface MetadataOption {
  label: string
  value: string
}

interface MetadataSelectProps {
  value: string | null | undefined
  options: MetadataOption[]
  placeholder: string
  loadFailedText?: string
  disabled?: boolean
  size?: "small" | "middle"
  className?: string
  autoFocus?: boolean
  allowClear?: boolean
  /** Forces the dropdown open — the Details panel's "click Edit to reveal" pattern. */
  open?: boolean
  onBlur?: () => void
  /** Returning `false` (e.g. the server rejected the change) reverts the
   *  visible selection back to `value` — otherwise a rejected change can be
   *  left showing on screen forever, since `value` itself never moved and a
   *  controlled antd `Select` only redraws its own picked option when the
   *  prop actually changes. */
  onChange: (value: string | null) => boolean | void | Promise<boolean | void>
  ariaLabel?: string
  /** A status color dot, a document-type icon — rendered beside both the
   *  closed value and every dropdown option, for whichever option's value
   *  it's given. Kept outside antd's own label/value plumbing (not
   *  `labelInValue`) so `option.label` stays a plain string and
   *  `optionFilterProp="label"` search keeps working untouched. */
  renderDecoration?: (optionValue: string) => React.ReactNode
}

/**
 * One Select configuration for every place a document's Status or Document
 * Type can be changed (the File List's always-visible cell, the Details
 * panel's click-to-edit row) — so there is exactly one status-editing and
 * one document-type-editing implementation, not a second one per surface.
 */
export function MetadataSelect({
  value,
  options,
  placeholder,
  loadFailedText,
  disabled,
  size,
  className,
  autoFocus,
  allowClear = true,
  open,
  onBlur,
  onChange,
  ariaLabel,
  renderDecoration
}: MetadataSelectProps) {
  // Bumped only when a change is rejected, to force the underlying antd
  // `Select` to remount — the one reliable way to make it redraw from
  // `value` when that prop's own value hasn't moved.
  const [resetKey, setResetKey] = useState(0)

  const handleChange = async (next: string | undefined) => {
    const result = await onChange(next ?? null)
    if (result === false) setResetKey((key) => key + 1)
  }

  return (
    <Select<string>
      key={resetKey}
      allowClear={allowClear}
      showSearch
      autoFocus={autoFocus}
      open={open}
      size={size}
      disabled={disabled}
      value={value ?? undefined}
      options={options}
      optionFilterProp="label"
      placeholder={placeholder}
      notFoundContent={loadFailedText}
      onBlur={onBlur}
      onChange={(next) => void handleChange(next)}
      // Rendered via `prefix`, not a sibling span, so the icon/color dot sits
      // inside the Select's own clickable box (antd's `.ant-select-selector`)
      // instead of beside it — a sibling span has no click handler of its
      // own, so clicking directly on it did nothing. Document Type's real
      // icon is a much bigger, more clickable-looking target than Status's
      // small color dot, which is why this surfaced as "Document Type
      // doesn't reliably open" even though both fields shared the same bug.
      prefix={value ? renderDecoration?.(value) : undefined}
      optionRender={
        renderDecoration
          ? (option) => (
              <span
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
              >
                {option.data.value
                  ? renderDecoration(String(option.data.value))
                  : null}
                {option.data.label}
              </span>
            )
          : undefined
      }
      className={className}
      aria-label={ariaLabel}
    />
  )
}
